import React from 'react';
import SalesDetailPage from './SalesDetailPage';
import { BOOKING_CONTACTS_COLUMNS, SORT_TYPES, getDefaultVisibleColumns, getDefaultHiddenColumns } from '../constants/bookingContactsColumnsConfig';
import { buildBookingContactsFilterGroups } from '../constants/bookingContactsFiltersConfig';

const buildDetailApiFilters = (activeFilters) => {
  const params = {};
  if (activeFilters.venue_ownership_id && activeFilters.venue_ownership_id.length > 0) {
    params.venue_ownership_id = activeFilters.venue_ownership_id;
  }
  if (activeFilters.contact_type && activeFilters.contact_type.length > 0) {
    params.contact_type = activeFilters.contact_type;
  }
  if (activeFilters.company_name && activeFilters.company_name.length > 0) {
    params.company_name = activeFilters.company_name;
  }
  if (activeFilters.auto_added && activeFilters.auto_added.length > 0) {
    params.auto_added = activeFilters.auto_added;
  }
  return params;
};

const BOOKING_CONTACTS_CONFIG = {
  pageTitle: 'Booking Contacts',
  pageSubtitle: 'Contact activity across your offers, holds, and events.',
  sidebarKey: 'booking_contacts',
  defaultPreset: 'year_to_date',
  dateModeOptions: [
    { key: 'offer_date', label: 'Offer Date' },
    { key: 'event_date', label: 'Event Date' },
  ],

  columns: BOOKING_CONTACTS_COLUMNS,
  sortTypes: SORT_TYPES,
  getDefaultVisibleColumns,
  getDefaultHiddenColumns,
  localStorageKey: (teamId) => `reportingBookingContacts_${teamId}`,
  firstColumnId: 'name',

  kpiFromDetailResponse: true,
  salesDataKey: null,
  venueSeriesKey: null,
  detailEndpoint: (teamId) => `/teams/${teamId}/reporting/booking/contacts/data.json`,
  detailResponseKey: 'contacts',
  exportBasePath: (teamId) => `/teams/${teamId}/reporting/booking/contacts/export`,
  detailErrorMessage: 'Failed to load contact data.',
  detailTableTitle: 'Contacts',
  emptyMessage: 'No contacts found.',
  searchPlaceholder: 'Search contacts...',
  paginationLabel: 'contacts',
  itemLabel: (count) => `contact${count !== 1 ? 's' : ''}`,

  buildDetailApiFilters,

  buildKpiCards: (current, prior) => [
    { label: 'Total Unique Contacts', value: (current.total_unique || 0).toLocaleString(), current: current.total_unique || 0, prior: prior.total_unique || 0 },
    { label: 'Contacts on Offers', value: (current.on_offers || 0).toLocaleString(), current: current.on_offers || 0, prior: prior.on_offers || 0 },
    { label: 'Contacts on Holds', value: (current.on_holds || 0).toLocaleString(), current: current.on_holds || 0, prior: prior.on_holds || 0 },
    { label: 'Contacts on Events', value: (current.on_confirms || 0).toLocaleString(), current: current.on_confirms || 0, prior: prior.on_confirms || 0 },
  ],
  kpiGridColumns: 4,

  overTimeKey: null,
  salesCardTitle: null,
  chartColor: 'var(--sl-fg-brand, #1982C4)',

  topCharts: [
    { title: 'Top 10 Contacts by Holds', dataKey: 'top_contacts_by_holds', barDataKey: 'count', nameKey: 'name', barColor: '#FF6F61', isCurrency: false, tooltipLabel: 'Holds' },
    { title: 'Top 10 Contacts by Offers', dataKey: 'top_contacts_by_offers', barDataKey: 'count', nameKey: 'name', barColor: 'var(--sl-fg-brand, #1982C4)', isCurrency: false, tooltipLabel: 'Offers' },
    { title: 'Top 10 Companies by Holds', dataKey: 'top_companies_by_holds', barDataKey: 'count', nameKey: 'name', barColor: '#FF6F61', isCurrency: false, tooltipLabel: 'Holds' },
    { title: 'Top 10 Companies by Offers', dataKey: 'top_companies_by_offers', barDataKey: 'count', nameKey: 'name', barColor: 'var(--sl-fg-brand, #1982C4)', isCurrency: false, tooltipLabel: 'Offers' },
    { title: 'Top 10 Contacts by Events', dataKey: 'top_contacts_by_confirms', barDataKey: 'count', nameKey: 'name', barColor: '#8AC926', isCurrency: false, tooltipLabel: 'Events' },
    { title: 'Top Unconverted Contacts', dataKey: 'top_unconverted_contacts', barDataKey: 'count', nameKey: 'name', barColor: '#E76F51', isCurrency: false, tooltipLabel: 'Offers + Holds' },
  ],

  groupByOptions: null,
  groupByDefault: null,

  renderCellValue: (col, contact) => {
    switch (col.id) {
      case 'name': return contact.name || '\u2014';
      case 'email': return contact.email || '\u2014';
      case 'phone': return contact.phone || '\u2014';
      case 'company_name': return contact.company_name || '\u2014';
      case 'contact_type': return contact.contact_type || '\u2014';
      case 'offer_count': return (contact.offer_count || 0).toLocaleString();
      case 'hold_count': return (contact.hold_count || 0).toLocaleString();
      case 'confirm_count': return (contact.confirm_count || 0).toLocaleString();
      default: return '\u2014';
    }
  },

  filterTitle: 'Filter Contacts',
  filterSubtitle: 'Apply filters to narrow contact data.',
};

function ReportingBookingContactsPage({ filterOptions, ...props }) {
  const configWithFilters = {
    ...BOOKING_CONTACTS_CONFIG,
    buildFilterGroups: (venueOwnerships) => buildBookingContactsFilterGroups(venueOwnerships, filterOptions || {}),
  };
  return <SalesDetailPage {...props} config={configWithFilters} />;
}

export default ReportingBookingContactsPage;
